import styles from './InviteGuestsFromHostedEvents.module.scss';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useGetMe, useGetUserHostedEventsV2Infinite } from 'legacy/user/hooks';
import { useInviteGuestsFromHostedEventMutation } from 'legacy/events/hooks';

import { showModal } from 'legacy/actions/modalActions';

import Modal from 'legacy/common/components/modal/Modal';

import * as toast from 'legacy/common/utils/toast';
import Loader from 'legacy/common/components/layout/Loader';
import { HostedEventDto } from 'legacy/events/types';
import { forwardRef, useCallback, useRef } from 'react';
import { DEFAULT_PAGE_LIMIT } from 'legacy/events/service';

type InviteGuestsFromHostedEventsProps = {
  destinationEventId: string;
  onClose: () => void;
};

const getMessage = (event: HostedEventDto) => {
  if (event.guestsCount === 1) {
    return 'confirmInviteOneGuest';
  }

  if (event.guestsCount === 2) {
    return 'confirmInviteTwoGuests';
  }

  return 'confirmInviteMultipleGuests';
};

const EventCard = forwardRef<
  HTMLDivElement,
  { event: HostedEventDto; inviteGuests: (event: HostedEventDto) => void }
>(({ event, inviteGuests }, ref) => {
  const { t } = useTranslation(['events', 'common']);
  return (
    <div
      key={event.id}
      ref={ref}
      className={styles.event}
      onClick={() => {
        inviteGuests(event);
      }}
    >
      <img src={event?.media[0]?.url} />
      <div className={styles.eventTitle}>{event?.title}</div>
      <div className={styles.guests}>
        {t('events:eventDetails.totalGuests', {
          count: event?.guestsCount
        })}
      </div>
    </div>
  );
});

const InviteGuestsFromHostedEvents: React.FC<
  InviteGuestsFromHostedEventsProps
> = ({ destinationEventId, onClose }) => {
  const { t } = useTranslation(['events', 'common']);
  const dispatch = useDispatch();
  const { mutate, isLoading: isLoadingInviteGuestsMutation } =
    useInviteGuestsFromHostedEventMutation();

  const ref = useRef<IntersectionObserver>(null);

  const { data: user, isFetching: isLoadingUser } = useGetMe();

  const {
    data,
    isFetching: isLoadingHostedEvents,
    fetchNextPage
  } = useGetUserHostedEventsV2Infinite({
    userId: user?._id,
    limit: DEFAULT_PAGE_LIMIT
  });

  const lastElementRef = useCallback(
    (node) => {
      if (isLoadingHostedEvents) {
        return;
      }

      if (ref.current) {
        ref.current.disconnect();
      }

      ref.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          data?.pages?.[data.pages.length - 1]?.items.length ===
            DEFAULT_PAGE_LIMIT
        ) {
          fetchNextPage({
            pageParam: data?.pages?.[data.pages.length - 1]?.nextOffset
          });
        }
      });

      if (node) {
        ref.current.observe(node);
      }
    },
    [isLoadingHostedEvents, data]
  );

  const inviteGuests = (event: HostedEventDto) => {
    dispatch(
      showModal('CONFIRMATION', {
        type: 'success',
        message: t(`events:invite.${getMessage(event)}`, {
          title: event.title,
          count: event.guestsCount
        }),
        onSuccess: ({ close }) => {
          close();

          mutate(
            {
              eventId: destinationEventId,
              sourceEventId: event.id
            },
            {
              onSuccess: () => {
                onClose();
                toast.success(
                  t('events:invite.successFromHostedEvent', {
                    title: event.title
                  })
                );
              },
              onError: (error) => {
                toast.error(error.message);
              }
            }
          );
        }
      })
    );
  };

  const previouslyHostedEvents = data?.pages
    ?.flatMap((page) => page.items)
    .filter(
      (event) =>
        event.id !== destinationEventId && event.guestsCount && !event.isDraft
    );

  return (
    <Modal visible onClose={onClose}>
      <Loader isLoading={isLoadingUser}>
        <div className={styles.wrapper}>
          <div className={styles.title}>
            {t('events:invite.inviteGuestsFromHostedEvent')}
          </div>
          <Loader isOverlay isLoading={isLoadingInviteGuestsMutation}>
            {previouslyHostedEvents?.length === 0 && (
              <div className={styles.empty}>
                {t('events:invite.noHostedEvents')}
              </div>
            )}

            <div className={styles.listWrapper}>
              {previouslyHostedEvents?.map((event, index) => {
                if (index === previouslyHostedEvents.length - 1) {
                  return (
                    <EventCard
                      key={event.id}
                      ref={lastElementRef}
                      event={event}
                      inviteGuests={inviteGuests}
                    />
                  );
                }

                return (
                  <EventCard
                    key={event.id}
                    event={event}
                    inviteGuests={inviteGuests}
                  />
                );
              })}
            </div>
            <Loader isLoading={isLoadingHostedEvents} />
          </Loader>
        </div>
      </Loader>
    </Modal>
  );
};

export default InviteGuestsFromHostedEvents;
